import { Stack } from '@mui/material';
import { MgSupplierItemCard } from '@retail/products/types';
import { formatPrice } from '@retail/products/utils';
import i18next from 'i18next';
import { useProductsTFunction } from '@retail/products/i18n';
import { ProductInformationCard } from './ProductInformationCard';
import { ProductInformationRows } from './ProductInformationRows';
import { getLLDate } from '@retail/utils';
import { ProductInformationRow } from './ProductInformationRow';
import { CopyToClipboard } from '@retail/components';

interface Props {
  selectedSupplierItem: MgSupplierItemCard;
}

export const ProductInformation = ({ selectedSupplierItem }: Props) => {
  const t = useProductsTFunction();

  return (
    <Stack>
      <Stack flexDirection="row" gap={1}>
        <ProductInformationCard title={t('products.mgItem.generally')} width="70%">
          <Stack px={2} py={1}>
            <ProductInformationRows
              rows={[
                {
                  name: t('products.mgItem.details.primaryText'),
                  value: selectedSupplierItem.primaryText,
                },
                {
                  name: t('products.mgItem.details.secondaryText'),
                  value: selectedSupplierItem.secondaryText,
                },
                {
                  name: t(
                    `products.mgItem.details.externalId.${selectedSupplierItem.externalSource}`
                  ),
                  value: (
                    <CopyToClipboard value={selectedSupplierItem.externalId}>
                      {selectedSupplierItem.externalId}
                    </CopyToClipboard>
                  ),
                },
                { name: t('products.mgItem.details.vatCode'), value: selectedSupplierItem.vatCode },
                {
                  name: t('products.mgItem.details.commodityCode'),
                  value: selectedSupplierItem.commodityCode,
                },
                {
                  name: t('products.mgItem.details.originCountry'),
                  value: selectedSupplierItem.originCountry,
                },
                {
                  name: t('products.mgItem.details.dangerousGoods'),
                  value: selectedSupplierItem.dangerousGoods,
                },
              ]}
              gridColumns="0.4fr 1.1fr"
            />
            <ProductInformationRow
              name={t('products.mgItem.details.textFull')}
              value={selectedSupplierItem.textFull}
              gridColumns="0.4fr 1.1fr"
              expandable
            />
          </Stack>
        </ProductInformationCard>

        <Stack flexDirection="column" width="30%" gap={1}>
          <ProductInformationCard title={t('products.mgItem.history')}>
            <Stack px={2} py={1}>
              <ProductInformationRows
                rows={[
                  {
                    name: t('products.mgItem.details.registrationDate'),
                    value: getLLDate(selectedSupplierItem.registrationDate),
                  },
                  {
                    name: t('products.mgItem.details.discontinuationDate'),
                    value: getLLDate(selectedSupplierItem.discontinuationDate),
                  },
                  {
                    name: t('products.mgItem.details.discontinuationCode'),
                    value: selectedSupplierItem.discontinuationCode,
                  },
                ]}
                gridColumns="1.2fr 0.8fr"
              />
            </Stack>
          </ProductInformationCard>

          <ProductInformationCard title={t('products.mgItem.details.basePrice')}>
            <Stack px={2} py={1}>
              <ProductInformationRows
                rows={[
                  {
                    name: t('products.mgItem.details.basePrice'),
                    value: selectedSupplierItem.basePrice
                      ? `${formatPrice(selectedSupplierItem.basePrice, i18next.language)}`
                      : '-',
                  },
                  {
                    name: t('products.mgItem.details.basePriceDate'),
                    value: getLLDate(selectedSupplierItem.basePriceDate),
                  },
                  {
                    name: t('products.mgItem.details.basePriceUnit'),
                    value: selectedSupplierItem.mgSupplierItemPackages.find(
                      (pkge) => pkge.basePriceUnit
                    )?.unit.description,
                  },
                ]}
                gridColumns="1fr 1fr"
              />
            </Stack>
          </ProductInformationCard>
        </Stack>
      </Stack>
      <Stack flexDirection="row" gap={1} pt={1}>
        <ProductInformationCard title={t('products.search.productGroup.label')} width="50%">
          <Stack px={2} py={1}>
            <ProductInformationRows
              rows={[
                {
                  name: t('products.mgItem.details.productGroup'),
                  value: selectedSupplierItem.productGroup?.groupNumber,
                },
                { name: t('products.mgItem.details.unspsc'), value: '-' },
                {
                  name: t('products.mgItem.details.discountGroup'),
                  value: selectedSupplierItem.discountGroup?.name,
                },
                {
                  name: t('products.mgItem.details.discountGroupDescription'),
                  value: selectedSupplierItem.discountGroup?.code,
                },
              ]}
              gridColumns="0.7fr 1fr"
            />
          </Stack>
        </ProductInformationCard>
        <ProductInformationCard title={t('products.supplier.name')} width="50%">
          <Stack px={2} py={1}>
            <ProductInformationRows
              rows={[
                {
                  name: t('products.mgItem.details.supplierId'),
                  value: selectedSupplierItem.mgSupplier.mgSupplierId,
                },
                {
                  name: t('products.mgItem.details.supplierProductName'),
                  value: selectedSupplierItem.supplierProductName,
                },
                {
                  name: t('products.mgItem.details.supplierItemId'),
                  value: selectedSupplierItem.supplierItemId,
                },
                {
                  name: t(
                    `products.mgItem.details.externalSupplierId.${selectedSupplierItem.externalSource}`
                  ),
                  value: selectedSupplierItem.externalSupplierId,
                },
              ]}
              gridColumns="0.7fr 1fr"
            />
          </Stack>
        </ProductInformationCard>
      </Stack>
    </Stack>
  );
};
